/**
 * Build Three.js segment data from Drillz geometry scene layers.
 */
import * as THREE from 'three';
import { enuToVector3 } from './geometryCoords';
import { mergeContinuousFeatures } from './geometryChannelAssignment';
import { colorForRow, radiusMultiplierForRow } from './geometryVisualRegistry';

/**
 * @param {import('three').Vector3} start
 * @param {import('three').Vector3} end
 */
export function segmentFromEndpoints(start, end) {
  const dir = new THREE.Vector3().subVectors(end, start);
  const length = dir.length();
  if (length < 1e-4) return null;
  dir.normalize();
  const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
  const up = new THREE.Vector3(0, 1, 0);
  const quat = new THREE.Quaternion().setFromUnitVectors(up, dir);
  return { start, end, midpoint, length, quaternion: quat };
}

/**
 * @param {object} feature
 * @param {{ easting: number, northing: number, elevation?: number }} anchor
 */
export function featureEndpoints(feature, anchor) {
  const positions = feature.positions || [];
  if (!positions.length) return null;
  const start = enuToVector3(positions[0], anchor);
  const end = positions.length > 1
    ? enuToVector3(positions[positions.length - 1], anchor)
    : start.clone();
  return segmentFromEndpoints(start, end);
}

/**
 * @param {object} scene Geometry scene response
 * @param {Map<string, object>} visualBundles keyed by tableDefinitionId
 * @param {Set<string>} activeLayerIds
 * @param {string|null} selectedHoleId
 * @param {{ easting: number, northing: number, elevation?: number }} anchor
 */
export function buildGeometrySegments(scene, visualBundles, activeLayerIds, selectedHoleId, anchor) {
  /** @type {Array<object>} */
  const segments = [];
  const layers = scene.layers || [];
  const traceByCollar = new Map(
    (scene.traces || []).map((t) => [t.collarUuid, t]),
  );
  const holeIdByCollar = new Map(
    (scene.collars || []).map((c) => [c.collarUuid, c.holeId]),
  );

  for (const layer of layers) {
    if (!activeLayerIds.has(layer.tableDefinitionId)) continue;
    const bundle = visualBundles.get(layer.tableDefinitionId);
    if (!bundle) continue;

    const mode = layer.spatialMode || 'interval';
    let features = layer.features || [];
    if (mode === 'interval') {
      features = mergeContinuousFeatures(features, bundle);
    }

    for (const feature of features) {
      const trace = traceByCollar.get(feature.collarUuid);
      const holeId = trace?.holeId || holeIdByCollar.get(feature.collarUuid) || null;
      const isSelected = !selectedHoleId || holeId === selectedHoleId;

      if (mode === 'point') {
        const pos = feature.positions?.[0];
        if (!pos) continue;
        const pt = enuToVector3(pos, anchor);
        segments.push({
          kind: 'point',
          layer,
          bundle,
          feature,
          holeId,
          isSelected,
          position: pt,
          color: colorForRow(feature.sourceRowId, bundle),
          radiusMul: radiusMultiplierForRow(feature.sourceRowId, bundle),
        });
        continue;
      }

      const seg = featureEndpoints(feature, anchor);
      if (!seg) continue;
      segments.push({
        kind: 'interval',
        layer,
        bundle,
        feature,
        holeId,
        isSelected,
        ...seg,
        color: colorForRow(feature.sourceRowId, bundle),
        radiusMul: radiusMultiplierForRow(feature.sourceRowId, bundle),
      });
    }
  }

  return segments;
}

/**
 * When hole_trace is empty, derive a display polyline from layer feature endpoints.
 * @param {object} scene
 * @param {string} collarUuid
 * @returns {[number, number, number][]|null} ENU positions
 */
export function syntheticTraceFromLayers(scene, collarUuid) {
  const points = [];
  const seen = new Set();
  const addPoint = (p) => {
    if (!p || p.length < 3) return;
    const key = `${p[0].toFixed(3)}:${p[1].toFixed(3)}:${p[2].toFixed(3)}`;
    if (seen.has(key)) return;
    seen.add(key);
    points.push(p);
  };

  for (const layer of scene.layers || []) {
    for (const f of layer.features || []) {
      if (f.collarUuid !== collarUuid) continue;
      addPoint(f.positions?.[0]);
      addPoint(f.positions?.[f.positions.length - 1]);
    }
  }

  if (points.length < 2) return null;
  points.sort((a, b) => (a[2] ?? 0) - (b[2] ?? 0));
  return points;
}

/**
 * @param {object} scene
 * @param {string|null} selectedHoleId
 * @param {{ easting: number, northing: number, elevation?: number }} anchor
 */
export function buildTraceLines(scene, selectedHoleId, anchor) {
  let traceList = scene.traces || [];
  if (!traceList.length && (scene.collars || []).length) {
    traceList = (scene.collars || []).map((c) => {
      const positions = syntheticTraceFromLayers(scene, c.collarUuid);
      if (!positions) return null;
      return {
        collarUuid: c.collarUuid,
        holeId: c.holeId,
        positions,
        depthsM: null,
      };
    }).filter(Boolean);
  }

  return traceList.map((trace) => {
    const points = (trace.positions || []).map((p) => enuToVector3(p, anchor));
    return {
      collarUuid: trace.collarUuid,
      holeId: trace.holeId,
      isSelected: trace.holeId === selectedHoleId,
      points,
      depthsM: trace.depthsM,
    };
  }).filter((t) => t.points.length >= 2);
}
